// src/utils/reporteOrdenServicioGenerator.js
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { obtenerClientePorId } from '@/api/clientes.js';
import { obtenerDatosEmpresa } from './tenantConfig';

// ==================== CONFIGURACIÓN ESTRICTA (igual que productos) ====================
export const JEAN_INFORMATICO_CONFIG = {
  nombre: "JEAN INFORMATICO C.A.",
  rif: "J-41023119-0",
  direccion: "Calle 30 con Av. 33, C.C. Latin Center P/B, Local 01 - Acarigua, Portuguesa",
  telefono: "0412-5696219 / 0416-7594723",
  whatsapp: "04167594723",
  ciudad: "Acarigua, Estado Portuguesa",
  email: "soportejeaninformatico@gmail.com | jeaninformaticoca@gmail.com",
  sitioWeb: "www.jeaninformatico.com.ve",
  slogan: "Servicio Técnico Especializado",
  lema: "Tecnología al servicio de la gente",
  instagram: "@jeaninformatico"
};

// ==================== CONFIG EMPRESA (con detección de placeholders) ====================
const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa() || {};
    return {
      // ✅ FIX: detecta placeholders y cae al fallback real
      nombre: (datos.nombreComercial && datos.nombreComercial !== 'MI EMPRESA' && datos.nombreComercial.trim() !== '')
        ? datos.nombreComercial
        : JEAN_INFORMATICO_CONFIG.nombre,
      rif: (datos.rif && datos.rif !== 'J-00000000-0')
        ? datos.rif
        : JEAN_INFORMATICO_CONFIG.rif,
      direccion: (datos.direccion && datos.direccion.trim() !== '')
        ? datos.direccion
        : JEAN_INFORMATICO_CONFIG.direccion,
      telefono: (datos.telefono1 && datos.telefono1.trim() !== '')
        ? `${datos.telefono1}${datos.telefono2 ? ' / ' + datos.telefono2 : ''}`
        : JEAN_INFORMATICO_CONFIG.telefono,
      whatsapp: datos.whatsapp || JEAN_INFORMATICO_CONFIG.whatsapp,
      ciudad: datos.ciudad || JEAN_INFORMATICO_CONFIG.ciudad,
      email: (datos.email && datos.email.trim() !== '')
        ? datos.email
        : JEAN_INFORMATICO_CONFIG.email,
      sitioWeb: datos.sitioWeb || JEAN_INFORMATICO_CONFIG.sitioWeb,
      slogan: datos.slogan || JEAN_INFORMATICO_CONFIG.slogan,
      lema: datos.lema || JEAN_INFORMATICO_CONFIG.lema,
      instagram: datos.instagram || JEAN_INFORMATICO_CONFIG.instagram,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    console.warn('⚠️ No se pudo obtener config del tenant, usando fallback:', err);
    return { ...JEAN_INFORMATICO_CONFIG, logoBase64: null };
  }
};

// ==================== LOGO ====================
let LOGO_BASE64 = '';
let LOGO_MIME = 'image/jpeg';

const detectarMimeDesdeBase64 = (b64) => {
  if (!b64) return 'image/jpeg';
  if (b64.startsWith('/9j/')) return 'image/jpeg';
  if (b64.startsWith('iVBORw0KGgo')) return 'image/png';
  if (b64.startsWith('R0lGOD')) return 'image/gif';
  if (b64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg';
};

const limpiarBase64 = (raw) => {
  if (!raw) return '';
  if (raw.includes(',')) return raw.split(',')[1];
  return raw;
};

export const cargarLogo = async () => {
  try {
    const empresa = getEmpresa();
    if (empresa.logoBase64) {
      const limpio = limpiarBase64(empresa.logoBase64);
      if (limpio && limpio.length > 50) {
        LOGO_BASE64 = limpio;
        LOGO_MIME = detectarMimeDesdeBase64(limpio);
        return LOGO_BASE64;
      }
    }
    const response = await fetch('/logo-jean.jpg');
    if (!response.ok) { LOGO_BASE64 = ''; return ''; }
    const blob = await response.blob();
    LOGO_MIME = blob.type || 'image/jpeg';
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = limpiarBase64(reader.result);
        resolve(LOGO_BASE64);
      };
      reader.onerror = () => { LOGO_BASE64 = ''; resolve(''); };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    LOGO_BASE64 = '';
    return '';
  }
};

export const getLogoBase64 = () => LOGO_BASE64;
export const getLogoSrc = () => LOGO_BASE64 ? `data:${LOGO_MIME};base64,${LOGO_BASE64}` : '';

// ==================== UTILIDADES ====================
export const formatearFechaTicket = (fechaStr) => {
  if (!fechaStr) return '—';
  try {
    return new Date(fechaStr).toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return fechaStr; }
};

export const formatearHoraTicket = (fechaStr) => {
  if (!fechaStr) return '—';
  try {
    return new Date(fechaStr).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return fechaStr; }
};

export const formatearMonedaUSD = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '$ 0.00';
  return `$ ${Number(valor).toFixed(2)}`;
};

export const formatearMonedaBs = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return 'Bs 0.00';
  return `Bs ${Number(valor).toFixed(2)}`;
};

export const formatearGarantiaMostrar = (dias) => {
  if (!dias || dias === 0) return '';
  if (dias >= 365) return `${Math.floor(dias / 365)} año${Math.floor(dias / 365) !== 1 ? 's' : ''}`;
  if (dias >= 30) return `${Math.floor(dias / 30)} mes${Math.floor(dias / 30) !== 1 ? 'es' : ''}`;
  return `${dias} día${dias !== 1 ? 's' : ''}`;
};

// ==================== OBTENER DATOS CLIENTE ====================
export const obtenerDatosClienteCompletos = async (orden) => {
  const idCliente = orden.idCliente || orden.IdCliente || orden.clienteId;
  let clienteData = null;
  if (idCliente) {
    try {
      const result = await obtenerClientePorId(idCliente);
      if (result.success && result.data) clienteData = result.data;
    } catch (error) {
      console.warn('⚠️ No se pudo obtener el cliente por ID:', error);
    }
  }
  if (clienteData) {
    return {
      nombre: `${clienteData.nombre || ''} ${clienteData.apellido || ''}`.trim().toUpperCase() || 'CONSUMIDOR FINAL',
      documento: clienteData.documento || '—',
      direccion: clienteData.direccion || '—',
      telefono: clienteData.telefono || '—'
    };
  }
  return {
    nombre: (orden.nombreCliente || orden.NombreCliente || 'CONSUMIDOR FINAL').toUpperCase(),
    documento: orden.documentoCliente || orden.DocumentoCliente || '—',
    direccion: orden.direccionCliente || orden.DireccionCliente || '—',
    telefono: orden.telefonoCliente || orden.TelefonoCliente || orden.telefono || '—'
  };
};

// ==================== GENERAR HTML ORDEN DE SERVICIO (MISMO ESTILO QUE PRODUCTOS) ====================
export const generarHTMLOrdenServicio = (orden, datosCliente) => {
  if (!orden) {
    return `<div style="padding:40px;text-align:center;">Orden no encontrada</div>`;
  }

  const empresa = getEmpresa();
  const logoSrc = getLogoSrc();
  const fechaActual = new Date();

  // Datos de la orden
  const numeroOrden = orden.numeroOrden || orden.NumeroOrden || '—';
  const fechaIngreso = orden.fechaIngreso || orden.FechaIngreso;
  const equipo = (orden.descripcion || orden.Descripcion || '—').toUpperCase();
  const problema = (orden.problema || orden.Problema || '—').toUpperCase();
  const accesorios = (orden.accesorios || orden.Accesorios || 'SIN ACCESORIOS').toUpperCase();
  const sede = orden.nombreSede || orden.NombreSede || orden.taller || orden.Taller || 'Local 01';
  const diagnostico = orden.tipoDiagnostico || orden.TipoDiagnostico || 'GRATIS';

  // Servicios y productos
  let servicios = orden.servicios || [];
  let productos = orden.productos || [];

  if (servicios.length === 0 && productos.length === 0 && orden.detalles) {
    const detalles = orden.detalles || [];
    servicios = detalles
      .filter(d => d.idServicio || d.idServicio > 0 || d.tipoItem === 'servicio' || (!d.idProducto && d.observaciones))
      .map(d => ({
        nombre: d.nombreServicio || d.observaciones || 'Servicio',
        precio: d.precioUnitarioUsd || d.subtotalUsd || 0,
        cantidad: d.cantidad || 1,
        garantia: d.garantiaDias || 0,
        garantiaDias: d.garantiaDias || 0
      }));
    productos = detalles
      .filter(d => d.idProducto || d.idProducto > 0 || d.tipoItem === 'producto')
      .map(d => ({
        nombre: d.nombreProducto || d.nombre || 'Producto',
        precio: d.precioUnitarioUsd || d.subtotalUsd || 0,
        cantidad: d.cantidad || 1,
        garantia: d.garantiaDias || 0,
        garantiaDias: d.garantiaDias || 0
      }));
  }

  if (servicios.length === 0 && productos.length === 0 && orden.recepcionEquipoDetalles) {
    const detalles = orden.recepcionEquipoDetalles || [];
    servicios = detalles
      .filter(d => d.idServicio || d.idServicio > 0 || (!d.idProducto && d.observaciones))
      .map(d => ({
        nombre: d.nombreServicio || d.observaciones || 'Servicio',
        precio: d.precioUnitarioUsd || d.subtotalUsd || 0,
        cantidad: d.cantidad || 1,
        garantia: d.garantiaDias || 0,
        garantiaDias: d.garantiaDias || 0
      }));
    productos = detalles
      .filter(d => d.idProducto || d.idProducto > 0)
      .map(d => ({
        nombre: d.nombreProducto || d.nombre || 'Producto',
        precio: d.precioUnitarioUsd || d.subtotalUsd || 0,
        cantidad: d.cantidad || 1,
        garantia: d.garantiaDias || 0,
        garantiaDias: d.garantiaDias || 0
      }));
  }

  // Totales
  const precioTotal = Number(orden.precioTotalUsd || orden.PrecioTotalUsd || 0);
  const descuento = Number(orden.descuento || orden.Descuento || 0);
  const montoPagadoRep = Number(orden.montoPagadoReparacionUsd || orden.MontoPagadoReparacionUsd || 0);
  const tasaBsUsd = Number(orden.tasaBsUsd || orden.TasaBsUsd || 0);
  const totalServicios = servicios.reduce((sum, s) => sum + (s.precio || 0), 0);
  const totalProductos = productos.reduce((sum, p) => sum + (p.precio || 0), 0);
  const subtotalCalculado = totalServicios + totalProductos;
  const totalMostrar = subtotalCalculado > 0 ? subtotalCalculado : precioTotal;
  const totalBs = Number((totalMostrar * tasaBsUsd).toFixed(2));
  const montoPagadoBs = Number((montoPagadoRep * tasaBsUsd).toFixed(2));

  const cliente = datosCliente || { nombre: 'CONSUMIDOR FINAL', telefono: '—' };

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Orden de Servicio ${numeroOrden} - ${empresa.nombre}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Arial', 'Helvetica', sans-serif; background: white; padding: 0; }
    
    .reporte { max-width: 780px; margin: 0 auto; padding: 2px 10px; }

    /* ENCABEZADO: idéntico al de productos */
    .header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      border-bottom: 2px solid #333;
      padding-bottom: 5px;
      margin-bottom: 8px;
    }
    .logo-container {
      flex-shrink: 0;
      width: 110px;
      display: flex;
      justify-content: center;
      padding-top: 4px;
    }
    .logo-img { max-height: 55px; max-width: 100px; object-fit: contain; }
    .header-center { flex: 1; text-align: center; padding: 0 5px; }
    .empresa-nombre { font-size: 14px; font-weight: bold; color: #222; margin-bottom: 1px; }
    .empresa-rif { font-size: 8.5px; color: #222; font-weight: bold; margin-bottom: 1px; }
    .empresa-contactos { font-size: 8px; color: #222; line-height: 1.15; font-weight: bold; }
    .empresa-direccion { font-size: 8.5px; color: #222; line-height: 1.2; margin-top: 4px; font-weight: bold; }

    /* TÍTULO ORDEN */
    .titulo-orden {
      text-align: center;
      margin-bottom: 6px;
      padding: 4px 0;
      border-top: 1px solid #ddd;
      border-bottom: 1px solid #ddd;
    }
    .titulo-orden h1 { font-size: 13px; font-weight: bold; color: #1a4d8c; text-transform: uppercase; margin-bottom: 1px; }
    .titulo-orden .orden-num { font-size: 11px; font-weight: bold; color: #000; }
    .titulo-orden .fecha { font-size: 7.5px; color: #666; }

    /* SECCIONES */
    .seccion {
      margin-bottom: 6px;
      border: 1px solid #ddd;
      page-break-inside: avoid;
    }
    .seccion-titulo {
      background: #f0f0f0;
      padding: 3px 6px;
      font-size: 8.5px;
      font-weight: bold;
      color: #333;
      text-transform: uppercase;
      border-bottom: 1px solid #ccc;
    }
    .seccion-contenido { padding: 4px 6px; font-size: 8.5px; line-height: 1.4; }

    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 12px; }
    .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px 12px; }

    .campo { display: flex; gap: 4px; }
    .campo-label { font-weight: bold; color: #555; min-width: 55px; }
    .campo-valor { color: #000; }

    /* TABLA DE DETALLE */
    table { width: 100%; border-collapse: collapse; font-size: 8px; border: 1px solid #ddd; table-layout: fixed; }
    th {
      background: #f0f0f0; padding: 3px 2px; text-align: left;
      font-weight: bold; border-bottom: 1px solid #ccc;
      color: #333; text-transform: uppercase; font-size: 7px;
    }
    td { padding: 3px 2px; border-bottom: 1px solid #eee; vertical-align: top; word-wrap: break-word; }
    tr { page-break-inside: avoid; }

    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .font-bold { font-weight: bold; }

    /* TOTALES */
    .totales {
      margin-top: 6px;
      display: flex;
      justify-content: flex-end;
      page-break-inside: avoid;
    }
    .total-box {
      border: 1px solid #333;
      padding: 4px 8px;
      min-width: 220px;
      background: #f8f9fa;
    }
    .total-linea { display: flex; justify-content: space-between; padding: 1px 0; font-size: 8.5px; }
    .total-linea.total-final {
      border-top: 2px solid #000;
      border-bottom: 2px solid #000;
      margin-top: 3px;
      padding: 3px 0;
      font-size: 10px;
      font-weight: bold;
    }

    /* FOOTER */
    .footer {
      margin-top: 8px; font-size: 7px; text-align: center;
      color: #666; padding-top: 3px; border-top: 1px solid #eee;
    }
    .footer strong { color: #cc0000; }

    @media print {
      @page { margin: 0.5cm; size: A4; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="reporte" id="reporte-pdf">

    <!-- ENCABEZADO DOBLE LOGO (idéntico a productos) -->
    <div class="header">
      <div class="logo-container">
        ${logoSrc ? `<img src="${logoSrc}" class="logo-img" alt="Logo Izq">` : ''}
      </div>
      <div class="header-center">
        <div class="empresa-nombre">${empresa.nombre}</div>
        <div class="empresa-rif">RIF: ${empresa.rif}</div>
        <div class="empresa-contactos">
          ${empresa.telefono}<br>
          ${empresa.email}
        </div>
        <div class="empresa-direccion">${empresa.direccion}</div>
      </div>
      <div class="logo-container">
        ${logoSrc ? `<img src="${logoSrc}" class="logo-img" alt="Logo Der">` : ''}
      </div>
    </div>

    <!-- TÍTULO ORDEN -->
    <div class="titulo-orden">
      <h1>Orden de Servicio Técnico</h1>
      <div class="orden-num">No. ${numeroOrden}</div>
      <div class="fecha">Fecha de Ingreso: ${formatearFechaTicket(fechaIngreso)}</div>
    </div>

    <!-- DATOS DEL CLIENTE -->
    <div class="seccion">
      <div class="seccion-titulo">Datos del Cliente</div>
      <div class="seccion-contenido">
        <div class="grid-2">
          <div class="campo"><span class="campo-label">Nombre:</span><span class="campo-valor">${cliente.nombre}</span></div>
          <div class="campo"><span class="campo-label">Documento:</span><span class="campo-valor">${cliente.documento || '—'}</span></div>
          <div class="campo"><span class="campo-label">Teléfono:</span><span class="campo-valor">${cliente.telefono || '—'}</span></div>
          <div class="campo"><span class="campo-label">Dirección:</span><span class="campo-valor">${cliente.direccion || '—'}</span></div>
        </div>
      </div>
    </div>

    <!-- DATOS DEL EQUIPO -->
    <div class="seccion">
      <div class="seccion-titulo">Datos del Equipo</div>
      <div class="seccion-contenido">
        <div class="grid-2">
          <div class="campo" style="grid-column: 1 / -1;"><span class="campo-label">Equipo:</span><span class="campo-valor">${equipo}</span></div>
          <div class="campo" style="grid-column: 1 / -1;"><span class="campo-label">Problema:</span><span class="campo-valor">${problema}</span></div>
          <div class="campo"><span class="campo-label">Accesorios:</span><span class="campo-valor">${accesorios}</span></div>
          <div class="campo"><span class="campo-label">Sede:</span><span class="campo-valor">${sede}</span></div>
          <div class="campo"><span class="campo-label">Diagnóstico:</span><span class="campo-valor">${diagnostico}</span></div>
        </div>
      </div>
    </div>

    <!-- DETALLE -->
    <div class="seccion">
      <div class="seccion-titulo">Detalle de Servicios y Productos</div>
      ${servicios.length > 0 || productos.length > 0 ? `
        <table>
          <colgroup>
            <col style="width: 60%;">
            <col style="width: 10%;">
            <col style="width: 15%;">
            <col style="width: 15%;">
          </colgroup>
          <thead>
            <tr>
              <th>Descripción</th>
              <th class="text-center">Cant.</th>
              <th class="text-right">Precio</th>
              <th class="text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${servicios.map(s => {
              const garantia = s.garantia || s.garantiaDias || 0;
              const precio = Number(s.precio || 0);
              const cantidad = Number(s.cantidad || 1);
              const nombre = (s.nombre || s.nombreServicio || s.observaciones || 'SERVICIO').toUpperCase();
              const garantiaTexto = garantia > 0 ? ` [GARANTÍA: ${formatearGarantiaMostrar(garantia).toUpperCase()}]` : '';
              return `
                <tr>
                  <td><strong>${nombre}</strong>${garantiaTexto}</td>
                  <td class="text-center">${cantidad}</td>
                  <td class="text-right">${formatearMonedaUSD(precio / cantidad)}</td>
                  <td class="text-right font-bold">${formatearMonedaUSD(precio)}</td>
                </tr>
              `;
            }).join('')}
            ${productos.map(p => {
              const garantia = p.garantia || p.garantiaDias || 0;
              const precio = Number(p.precio || 0);
              const cantidad = Number(p.cantidad || 1);
              const nombre = (p.nombre || p.nombreProducto || 'PRODUCTO').toUpperCase();
              const garantiaTexto = garantia > 0 ? ` [GARANTÍA: ${formatearGarantiaMostrar(garantia).toUpperCase()}]` : '';
              return `
                <tr>
                  <td><strong>${nombre}</strong>${garantiaTexto}</td>
                  <td class="text-center">${cantidad}</td>
                  <td class="text-right">${formatearMonedaUSD(precio / cantidad)}</td>
                  <td class="text-right font-bold">${formatearMonedaUSD(precio)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      ` : `
        <div class="seccion-contenido text-center" style="color:#666;padding:6px 0;">SIN SERVICIOS REGISTRADOS</div>
      `}
    </div>

    <!-- TOTALES -->
    <div class="totales">
      <div class="total-box">
        ${descuento > 0 ? `
          <div class="total-linea"><span>Subtotal:</span><span>${formatearMonedaUSD(totalMostrar + descuento)}</span></div>
          <div class="total-linea"><span>Descuento:</span><span>-${formatearMonedaUSD(descuento)}</span></div>
        ` : ''}
        <div class="total-linea total-final">
          <span>TOTAL USD:</span>
          <span>${formatearMonedaUSD(totalMostrar)}</span>
        </div>
        ${tasaBsUsd > 0 ? `
          <div class="total-linea"><span>Tasa Bs/USD:</span><span>${tasaBsUsd.toFixed(2)}</span></div>
          <div class="total-linea font-bold"><span>TOTAL Bs:</span><span>${formatearMonedaBs(totalBs)}</span></div>
        ` : ''}
        ${montoPagadoRep > 0 ? `
          <div class="total-linea" style="border-top:1px solid #ddd;margin-top:3px;padding-top:3px;">
            <span>Pagado USD:</span><span>${formatearMonedaUSD(montoPagadoRep)}</span>
          </div>
          ${tasaBsUsd > 0 ? `
            <div class="total-linea"><span>Pagado Bs:</span><span>${formatearMonedaBs(montoPagadoBs)}</span></div>
          ` : ''}
        ` : ''}
      </div>
    </div>

    <!-- FOOTER -->
    <div class="footer">
      <div style="font-size:8px;font-weight:bold;color:#000;margin-bottom:2px;">¡GRACIAS POR SU CONFIANZA!</div>
      <div>${empresa.nombre} • RIF: ${empresa.rif}</div>
      <div>Síguenos: <strong>${empresa.instagram || '@jeaninformatico'}</strong> • ${empresa.slogan}</div>
    </div>
  </div>
</body>
</html>`;
};

// ==================== FUNCIÓN PRINCIPAL - DESCARGAR PDF ====================
export const descargarOrdenServicioPDF = async (orden) => {
  if (!orden) {
    alert('No hay orden para generar PDF');
    return false;
  }

  try {
    LOGO_BASE64 = '';
    LOGO_MIME = 'image/jpeg';
    await cargarLogo();

    const datosCliente = await obtenerDatosClienteCompletos(orden);
    const numeroOrden = orden.numeroOrden || orden.NumeroOrden || 'SIN_NUMERO';
    const nombreArchivo = `ORDEN_${numeroOrden}.pdf`;
    const htmlContent = generarHTMLOrdenServicio(orden, datosCliente);

    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:800px;background:white;padding:0;z-index:-1;';
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    // Esperar a que carguen las imágenes
    await new Promise(resolve => setTimeout(resolve, 600));

    const elemento = container.querySelector('#reporte-pdf');

    const canvas = await html2canvas(elemento, {
      scale: 2.5,
      useCORS: true,
      width: 780,
      height: elemento.scrollHeight,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = pdfHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
    heightLeft -= pdf.internal.pageSize.getHeight();

    while (heightLeft > 0) {
      position = heightLeft - pdfHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();
    }

    pdf.save(nombreArchivo);
    document.body.removeChild(container);
    return true;
  } catch (error) {
    console.error('Error generando PDF:', error);
    alert('Error al generar el PDF: ' + error.message);
    return false;
  }
};

export default {
  JEAN_INFORMATICO_CONFIG,
  cargarLogo,
  getLogoBase64,
  getLogoSrc,
  formatearFechaTicket,
  formatearHoraTicket,
  formatearMonedaUSD,
  formatearMonedaBs,
  formatearGarantiaMostrar,
  obtenerDatosClienteCompletos,
  generarHTMLOrdenServicio,
  descargarOrdenServicioPDF
};